import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { CloseIcon, SearchIcon } from "../SvgIcons";

/**
 * SearchSelect — a searchable select input with single or multi-value support.
 *
 * Props:
 *   options          {Array}    — [{ label, value }] or flat string[]. Required.
 *   value            {any}      — selected value (string/object) for single; array for multiple
 *   onChange         {Function} — called with selected item (single) or array (multiple)
 *   multiple         {boolean}  — enable multi-select (default false)
 *   placeholder      {string}   — trigger placeholder text
 *   searchPlaceholder{string}   — search input placeholder
 *   disabled         {boolean}
 *   loading          {boolean}
 *   className        {string}   — extra class on the root element
 *   id               {string}
 *   maxHeight        {number}   — max height of the options list in px (default 240)
 *   allowClear       {boolean}  — show a clear-all button when values are selected
 *   labelKey         {string}   — key to read label from option objects (default "label")
 *   valueKey         {string}   — key to read value from option objects (default "value")
 *   includeSelectAll {boolean}  — show a "Select all" row (multiple only, default false)
 *   selectAllText    {string}   — label for the select-all row (default "Select All")
 *   maxTagsDisplayed {number}   — cap visible tags before collapsing the rest into "+N more" (multiple only, default 6)
 */
// Height of each .ss-option row in px — must match the CSS
const ITEM_HEIGHT = 37;
// Extra rows rendered above and below the visible window
const VIRTUAL_BUFFER = 4;

const SearchSelect = ({
	options = [],
	value,
	onChange,
	multiple = false,
	placeholder = "Select...",
	searchPlaceholder = "Search...",
	disabled = false,
	loading = false,
	className = "",
	id,
	maxHeight = 240,
	allowClear = false,
	labelKey = "label",
	valueKey = "value",
	includeSelectAll = false,
	selectAllText = "Select All",
	maxTagsDisplayed = 3,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [dropdownStyle, setDropdownStyle] = useState({});

	const triggerRef = useRef(null);
	const dropdownRef = useRef(null);
	const searchRef = useRef(null);
	const listRef = useRef(null);
	const [scrollTop, setScrollTop] = useState(0);

	// ── Option helpers ─────────────────────────────────────────────────────────

	const normalizeOption = useCallback(
		(opt) => {
			if (opt === null || opt === undefined) return null;
			if (typeof opt !== "object") return { label: String(opt), value: opt };
			return { label: opt[labelKey] ?? String(opt[valueKey] ?? ""), value: opt[valueKey] };
		},
		[labelKey, valueKey],
	);

	const normalizedOptions = useMemo(
		() => options.map(normalizeOption).filter(Boolean),
		[options, normalizeOption],
	);

	// ── Selection state ────────────────────────────────────────────────────────

	const selectedValues = useMemo(() => {
		if (!multiple) {
			if (value === null || value === undefined || value === "") return [];
			const v = typeof value === "object" ? value[valueKey] : value;
			const match = normalizedOptions.find((o) => o.value === v);
			return match ? [match] : [{ label: String(v), value: v }];
		}
		const arr = Array.isArray(value) ? value : [];
		return arr
			.filter((item) => item !== null && item !== undefined)
			.map((item) => {
				const v = typeof item === "object" ? item[valueKey] : item;
				const match = normalizedOptions.find((o) => o.value === v);
				return match ?? { label: String(v), value: v };
			})
			.filter(Boolean);
	}, [value, multiple, normalizedOptions, valueKey]);

	const isSelected = useCallback(
		(opt) => selectedValues.some((s) => s.value === opt.value),
		[selectedValues],
	);

	// ── Filtered options ───────────────────────────────────────────────────────

	const filteredOptions = useMemo(() => {
		if (!search.trim()) return normalizedOptions;
		const q = search.toLowerCase();
		return normalizedOptions.filter((o) => String(o.label).toLowerCase().includes(q));
	}, [normalizedOptions, search]);

	// ── Dropdown positioning (portal) ──────────────────────────────────────────

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom - 8;
		const spaceAbove = rect.top - 8;
		const goUp = spaceBelow < 200 && spaceAbove > spaceBelow;
		setDropdownStyle({
			position: "fixed",
			left: rect.left,
			width: rect.width,
			zIndex: 10001,
			maxHeight: Math.min(maxHeight + 52, goUp ? spaceAbove : spaceBelow), // +52 for search row
			...(goUp ? { bottom: window.innerHeight - rect.top } : { top: rect.bottom + 2 }),
		});
	}, [maxHeight]);

	useEffect(() => {
		if (!isOpen) return;
		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		// Layout can shift the trigger (e.g. a save banner appearing above it)
		// without firing a scroll or resize event — keep the fixed-position
		// dropdown glued to the trigger by re-measuring every frame.
		let rafId = requestAnimationFrame(function tick() {
			updatePosition();
			rafId = requestAnimationFrame(tick);
		});

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
			cancelAnimationFrame(rafId);
		};
	}, [isOpen, updatePosition]);

	// ── Outside click ──────────────────────────────────────────────────────────

	useEffect(() => {
		if (!isOpen) return;
		const handler = (e) => {
			if (
				!triggerRef.current?.contains(e.target) &&
				!dropdownRef.current?.contains(e.target)
			) {
				close();
			}
		};
		document.addEventListener("mousedown", handler, true);
		return () => document.removeEventListener("mousedown", handler, true);
	}, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset scroll position when the filtered list changes
	useEffect(() => {
		setScrollTop(0);
		if (listRef.current) listRef.current.scrollTop = 0;
	}, [filteredOptions]);

	// ── Keyboard ───────────────────────────────────────────────────────────────

	const handleTriggerKeyDown = (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			toggle();
		} else if (e.key === "Escape") {
			close();
		} else if (e.key === "ArrowDown" && !isOpen) {
			open();
		}
	};

	// ── Open / close ───────────────────────────────────────────────────────────

	function open() {
		if (disabled || loading) return;
		// Compute the fixed position synchronously, in the same batch as
		// setIsOpen, so the portalled dropdown never paints an unpositioned
		// frame at the bottom of <body>. Left unpositioned even briefly, the
		// search input's autoFocus makes the browser scroll the page down to
		// bring that (temporarily) off-screen input into view.
		updatePosition();
		setIsOpen(true);
		setSearch("");
		setScrollTop(0);
	}

	function close() {
		setIsOpen(false);
		setSearch("");
	}

	function toggle() {
		isOpen ? close() : open();
	}

	// ── Selection ──────────────────────────────────────────────────────────────

	function select(opt) {
		if (multiple) {
			const already = isSelected(opt);
			const next = already
				? selectedValues.filter((s) => s.value !== opt.value)
				: [...selectedValues, opt];
			onChange?.(next.map((s) => s.value));
		} else {
			onChange?.(opt.value);
			close();
		}
	}

	const showSelectAll = includeSelectAll && multiple && filteredOptions.length > 0;
	const allFilteredSelected = showSelectAll && filteredOptions.every(isSelected);

	function toggleSelectAll(e) {
		e.stopPropagation();
		if (allFilteredSelected) {
			const remove = new Set(filteredOptions.map((o) => o.value));
			const next = selectedValues.filter((s) => !remove.has(s.value));
			onChange?.(next.map((s) => s.value));
		} else {
			const merged = [...selectedValues];
			filteredOptions.forEach((o) => {
				if (!merged.some((s) => s.value === o.value)) merged.push(o);
			});
			onChange?.(merged.map((s) => s.value));
		}
	}

	function clearAll(e) {
		e.stopPropagation();
		onChange?.(multiple ? [] : null);
	}

	function removeTag(e, opt) {
		e.stopPropagation();
		const next = selectedValues.filter((s) => s.value !== opt.value);
		onChange?.(next.map((s) => s.value));
	}

	// ── Render ─────────────────────────────────────────────────────────────────

	const hasValue = selectedValues.length > 0;

	return (
		<div
			id={id}
			className={`ss-root${disabled ? " ss-root--disabled" : ""}${loading ? " ss-root--loading" : ""} ${className}`}>
			{/* Trigger */}
			<div
				ref={triggerRef}
				className={`ss-trigger${isOpen ? " ss-trigger--open" : ""}${!hasValue ? " ss-trigger--empty" : ""}`}
				tabIndex={disabled || loading ? -1 : 0}
				role="combobox"
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				onClick={toggle}
				onKeyDown={handleTriggerKeyDown}>
				<div className="ss-trigger__values">
					{!hasValue && (
						<span className="ss-placeholder">{placeholder}</span>
					)}
					{hasValue && multiple && selectedValues.slice(0, maxTagsDisplayed).map((opt) => (
						<span key={opt.value} className="ss-tag">
							<span className="ss-tag__label">{opt.label}</span>
							<span
								className="ss-tag__remove"
								role="button"
								aria-label={`Remove ${opt.label}`}
								tabIndex={0}
								onClick={(e) => removeTag(e, opt)}
								onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && removeTag(e, opt)}>
								×
							</span>
						</span>
					))}
					{hasValue && multiple && selectedValues.length > maxTagsDisplayed && (
						<span className="ss-tag ss-tag--more">
							+{selectedValues.length - maxTagsDisplayed} more
						</span>
					)}
					{hasValue && !multiple && (
						<span className="ss-single-value">{selectedValues[0].label}</span>
					)}
				</div>
				<div className="ss-trigger__actions">
					{allowClear && hasValue && !disabled && !loading && (
						<button
							type="button"
							className="ss-clear-btn"
							onClick={clearAll}
							aria-label="Clear selection">
							<CloseIcon size={12} />
						</button>
					)}
					{loading ? (
						<span className="ss-spinner" aria-hidden="true" />
					) : (
						<div className="fe-select-arrow" />
					)}
				</div>
			</div>

			{/* Dropdown — portalled to avoid overflow clipping */}
			{isOpen && ReactDOM.createPortal(
				<div
					ref={dropdownRef}
					className={`ss-dropdown${dropdownStyle.bottom ? " ss-dropdown--up" : " ss-dropdown--down"}`}
					style={dropdownStyle}
					role="listbox">
					{/* Search */}
					<div className="ss-search">
						<SearchIcon size={14} className="ss-search__icon" />
						<input
							ref={searchRef}
							type="text"
							className="ss-search__input"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={searchPlaceholder}
							autoFocus
							onKeyDown={(e) => e.key === "Escape" && close()}
						/>
						{search && (
							<button
								type="button"
								className="ss-search__clear"
								onClick={() => setSearch("")}
								aria-label="Clear search">
								<CloseIcon size={12} />
							</button>
						)}
					</div>

					{/* Select all */}
					{!loading && showSelectAll && (
						<div
							className={`ss-option ss-select-all${allFilteredSelected ? " ss-option--selected" : ""}`}
							style={{ height: ITEM_HEIGHT, boxSizing: "border-box" }}
							role="option"
							aria-selected={allFilteredSelected}
							onMouseDown={(e) => {
								e.preventDefault();
								toggleSelectAll(e);
							}}>
							<input
								type="checkbox"
								readOnly
								checked={allFilteredSelected}
								tabIndex={-1}
								className="ss-checkbox"
							/>
							<span className="ss-option__label">{selectAllText}</span>
						</div>
					)}

					{/* Options — virtualised so large lists don't freeze */}
					<div
						ref={listRef}
						className="ss-options"
						style={{ maxHeight, overflowY: "auto" }}
						onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
						{loading ? (
							<div className="ss-state">Loading...</div>
						) : filteredOptions.length === 0 ? (
							<div className="ss-state">No options found</div>
						) : (() => {
							const startIdx = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - VIRTUAL_BUFFER);
							const endIdx   = Math.min(filteredOptions.length, Math.ceil((scrollTop + maxHeight) / ITEM_HEIGHT) + VIRTUAL_BUFFER);
							const paddingTop    = startIdx * ITEM_HEIGHT;
							const paddingBottom = (filteredOptions.length - endIdx) * ITEM_HEIGHT;
							return (
								<>
									{paddingTop > 0 && <div style={{ height: paddingTop }} />}
									{filteredOptions.slice(startIdx, endIdx).map((opt) => {
										const selected = isSelected(opt);
										return (
											<div
												key={opt.value}
												className={`ss-option${selected ? " ss-option--selected" : ""}`}
												style={{ height: ITEM_HEIGHT, boxSizing: "border-box" }}
												role="option"
												aria-selected={selected}
												onMouseDown={(e) => {
													e.preventDefault();
													select(opt);
												}}>
												{multiple && (
													<input
														type="checkbox"
														readOnly
														checked={selected}
														tabIndex={-1}
														className="ss-checkbox"
													/>
												)}
												<span className="ss-option__label">{opt.label}</span>
												{!multiple && selected && (
													<i className="icon-ok ss-option__check" />
												)}
											</div>
										);
									})}
									{paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
								</>
							);
						})()}
					</div>
				</div>,
				document.body,
			)}
		</div>
	);
};

export default SearchSelect;
